import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isUndefined from 'lodash/isUndefined';
import { Experiment, Variant } from '@marvelapp/react-ab-test';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import {
  makeSelectProfile,
  makeSelectProfileFilters,
} from 'containers/Auth/selectors';
import { getSlimItems } from 'containers/EditorList/utils';
import {
  insuranceOptionsSelector,
  makeSelectAudienceTagsRelations,
  makeSelectClientDetails,
} from 'containers/Main/selectors';
import {
  getListsWithResources,
  getEvidenceBaseApproach,
  getClientDetailsExcludedTopics,
} from 'containers/TopicCenter/utils';
import getTopicEvidence from 'containers/TopicCenter/services/getTopicEvidences';
import getTopicListResources from 'containers/TopicCenter/services/getTopicListResources';
import SurveyResultContext from 'components/SurveyResult/SurveyResultContext';
import useAlgoliaLocale from 'components/useAlgoliaLocale';
import useContentfulLocale from 'components/useContentfulLocale';
import { getProfileFiltersFromAnswers } from 'components/Hooks/useProfileFilters/utils';
import useSiteConfigSelector from 'components/useSiteConfigSelector';
import useSiteCopySelector from 'components/useSiteCopySelector';
import useClientExcludedResourceType from 'components/Hooks/useClientExcludedResourceType';
import useExcludedArticleTypes from 'components/Hooks/useExcludedArticleTypes';
import GroupedLists from './GroupedLists';
import TabbedLists from './TabbedLists';
import { filterTopicsByExcludedTopics } from '../../utils';

const useStyles = makeStyles({
  container: {
    paddingTop: 16,
    paddingBottom: 20,
  },
});

const RegularComponentLists = ({
  assessment,
  entryId,
  listsStyle,
  isFullWidth,
  lists,
  handPickedResources,
  topics,
  labelValue,
}) => {
  const classes = useStyles();
  const [insuranceSiteConfig, featuresConfig] = useSiteConfigSelector([
    'Insurance',
    'Features',
  ]);
  const [
    assessmentResultsSiteCopy,
    resourceItemSiteCopy,
    filtersTranslationsSiteCopy,
  ] = useSiteCopySelector([
    'assessment-results',
    'resource-item-page',
    'filters-translations',
  ]);
  const insuranceOptions = useSelector(insuranceOptionsSelector);
  const profile = useSelector(makeSelectProfile());
  const profileFilters = useSelector(makeSelectProfileFilters());
  const audienceTagsRelations = useSelector(makeSelectAudienceTagsRelations());
  const clientDetails = useSelector(makeSelectClientDetails());
  const algoliaLocale = useAlgoliaLocale();
  const ctfLocaleFilter = useContentfulLocale();
  const {
    answer,
    answersMatchedAudiences,
    answersMatchedTags,
    excludedTopicId,
  } = useContext(SurveyResultContext);

  const { isResourceTypeExcluded } = useClientExcludedResourceType();
  const { isArticleTypesExcluded } = useExcludedArticleTypes();

  const finalListsStyle =
    listsStyle || _get(assessment, 'fields.resultsListStyle');

  const slimItems = getSlimItems({
    lists,
    resourceItemSiteCopy,
    assessmentSlug: _get(assessment, 'fields.slug'),
  });

  const clientShortName = _get(clientDetails, 'shortName');
  const clientExcludedTopic = _get(
    clientDetails,
    'excludeTopicCollection.items',
  );
  const clientExcludedResourceTypes =
    _get(clientDetails, 'excludeResourceTypes') || [];
  const clientExcludedCost = _get(
    clientDetails,
    'metadata.hideCostDescriptionResources',
  );
  const clientListResourcesWeights = _get(
    clientDetails,
    'metadata.listResourcesWeights',
  );
  const averageRatingCutoff = _get(clientDetails, 'averageRatingCutoff');
  const expertRatingCutoff = _get(clientDetails, 'expertRatingCutoff');
  const userRatingCutoff = _get(clientDetails, 'userRatingCutoff');
  const resourcesWeightings = _get(
    clientDetails,
    'metadata.algoliaResourcesWeight',
  );
  const excludedAssessments = _get(
    clientDetails,
    'excludeAssessmentCollection.items',
  );
  const prioritizeClientResources = _get(
    clientDetails,
    'metadata.prioritizeClientResources',
    false,
  );
  const shouldFilterShortArticles = _get(
    clientDetails,
    'metadata.filterShortArticles',
    false,
  );

  const { data: evidence } = useQuery(
    !_isUndefined(_get(topics, '0.sys.id')) && [
      'evidence',
      topics[0].sys.id,
      ctfLocaleFilter,
    ],
    getTopicEvidence,
  );

  const relatedTopics = _get(topics[0], 'fields.relatedTopics', []);
  const childTopics = _get(topics[0], 'fields.childTopics', []);
  const hasRelatedTopics = !_isEmpty(relatedTopics);
  const acceptedTopics = hasRelatedTopics
    ? relatedTopics.filter(
        ({ fields }) =>
          _get(fields, 'reviewStatus') === 'Accepted' &&
          _get(fields, 'approach') === true,
      )
    : {};
  const approaches = getEvidenceBaseApproach({
    topics: acceptedTopics,
    evidence,
    types: childTopics,
    excludedTopicSlugs: getClientDetailsExcludedTopics(clientDetails),
  });

  const finalProfileFilters = !profile.isEmpty
    ? profileFilters
    : getProfileFiltersFromAnswers({
        audiences: answersMatchedAudiences,
        tags: answersMatchedTags,
        audienceTagsRelations,
        insuranceOptions,
        enforcedInsuranceOptions: _get(
          insuranceSiteConfig,
          ['config', 'enforcedInsuranceTags', clientShortName],
          [],
        ),
        insuranceLabel: _get(insuranceSiteConfig, [
          'config',
          'label',
          algoliaLocale,
        ]),
        state: _get(answer, 'stateOfResidence'),
      });

  const { data: resources } = useQuery(
    slimItems && [
      'assessment-results-tabbed-lists',
      {
        slimItems,
        profileFilters: finalProfileFilters,
        clientDetails,
        clientExcludedTopic,
        clientExcludedResourceTypes,
        clientListResourcesWeights,
        averageRatingCutoff,
        expertRatingCutoff,
        userRatingCutoff,
        resourcesWeightings,
        excludedAssessments,
        algoliaLocale,
        prioritizeClientResources,
        filtersTranslationsSiteCopy,
      },
    ],
    getTopicListResources,
  );

  const dataWithResources = getListsWithResources({
    lists,
    resources,
    clientShortName,
    clientGroupShortName: _get(clientDetails, 'clientGroup.shortName'),
    profileFilters: finalProfileFilters,
    clientExcludedTopics: clientExcludedTopic,
    clientExcludedResourceTypes,
    clientExcludedAssessments: excludedAssessments,
    clientExcludedCost,
    averageRatingCutoff,
    userRatingCutoff,
    expertRatingCutoff,
    prioritizeClientResources,
    featuresConfig,
    shouldFilterShortArticles,
    clientDetails,
  })
    .filter(
      list =>
        filterTopicsByExcludedTopics(
          _get(list, 'resources', []),
          excludedTopicId,
        ).length > 0,
    )
    .map(list => {
      const listResources = _get(list, 'resources', []);
      const filteredListResources = listResources.filter(
        resource =>
          !isResourceTypeExcluded(_get(resource, 'sys.contentType.sys.id')) &&
          !_isEmpty(isArticleTypesExcluded(resource)),
      );

      return {
        ...list,
        resources: filteredListResources,
      };
    })
    .filter(list => !_isEmpty(list.resources));

  if (
    !_isEmpty(handPickedResources) &&
    ((lists.length === 0 && finalListsStyle === 'Tabbed lists') ||
      finalListsStyle === 'Grouped lists')
  ) {
    dataWithResources.push({
      type: 'handpicked',
      item: {
        fields: {
          slug: 'handpicked',
          title: _get(assessmentResultsSiteCopy, 'pageCopy.handpickedTabLabel'),
        },
      },
      resources: filterTopicsByExcludedTopics(
        handPickedResources,
        excludedTopicId,
      ),
    });
  }
  if (
    !_get(assessment, 'fields.globalOptions.hideApproaches', false) &&
    !_isEmpty(approaches.all)
  ) {
    dataWithResources.push({
      type: 'view-all',
      item: {
        fields: {
          slug: 'view-all',
          title: _get(assessmentResultsSiteCopy, 'pageCopy.whatHelpsTabLabel'),
        },
      },
      resources: filterTopicsByExcludedTopics(
        _get(approaches, 'all', []),
        excludedTopicId,
      ),
    });
  }

  if (_isEmpty(lists)) return null;

  const name = _get(assessment, 'fields.name');
  const isListsStyleSet = !!finalListsStyle;

  return (
    <Grid
      container
      spacing={2}
      className={`${classes.container} assessment-result-list`}
    >
      {isListsStyleSet ? (
        <>
          {finalListsStyle === 'Grouped lists' && (
            <GroupedLists
              data={dataWithResources}
              assessment={assessment}
              entryId={entryId}
              isFullWidth={isFullWidth}
              topic={topics[0]}
              clientExclusiveAsResource={prioritizeClientResources}
              labelValue={labelValue}
            />
          )}
          {finalListsStyle === 'Tabbed lists' && (
            <TabbedLists
              labelValue={labelValue}
              data={dataWithResources}
              assessmentName={name}
              entryId={entryId}
              isFullWidth={isFullWidth}
              topic={topics[0]}
              clientExclusiveAsResource={prioritizeClientResources}
            />
          )}
        </>
      ) : (
        <Experiment name="assessment-results-lists">
          <Variant name="grouped">
            <GroupedLists
              data={dataWithResources}
              assessment={assessment}
              entryId={entryId}
              isFullWidth={isFullWidth}
              topic={topics[0]}
              clientExclusiveAsResource={prioritizeClientResources}
              labelValue={labelValue}
            />
          </Variant>
          <Variant name="tabbed">
            <TabbedLists
              labelValue={labelValue}
              data={dataWithResources}
              assessmentName={name}
              entryId={entryId}
              isFullWidth={isFullWidth}
              topic={topics[0]}
              clientExclusiveAsResource={prioritizeClientResources}
            />
          </Variant>
        </Experiment>
      )}
    </Grid>
  );
};

RegularComponentLists.propTypes = {
  isFullWidth: PropTypes.bool,
  lists: PropTypes.array,
  handPickedResources: PropTypes.array,
  assessment: PropTypes.object,
  topics: PropTypes.array,
  entryId: PropTypes.string,
  listsStyle: PropTypes.string,
  labelValue: PropTypes.string,
};

export default RegularComponentLists;
